/**
 * Enhanced Payee Cache Service with Session-Based Caching and Performance Optimization
 * 
 * Optimized for autocomplete functionality with <50ms response times
 */

import cacheService, { CacheKeys } from './cacheService';

/**
 * Payee cache configuration
 */
const PAYEE_CACHE_CONFIG = {
  // Session-based cache TTL (longer than other caches)
  SESSION_TTL: 30 * 60 * 1000,        // 30 minutes
  AUTOCOMPLETE_TTL: 5 * 60 * 1000,    // 5 minutes for autocomplete results
  
  // Performance optimization
  MIN_SEARCH_LENGTH: 1,               // Minimum characters to trigger search
  MAX_RESULTS: 20,                    // Maximum autocomplete results
  DEBOUNCE_DELAY: 150,                // Debounce delay in ms
  
  // Prefetch configuration
  PREFETCH_ENABLED: true,
  PREFETCH_DELAY: 2000,               // 2 seconds after initial load
  
  // Usage tracking for intelligent caching
  USAGE_TRACKING: true,
  POPULAR_THRESHOLD: 5                // Usage count to consider payee "popular"
};

/**
 * Payee cache keys
 */
const PayeeCacheKeys = {
  ALL_PAYEES: () => CacheKeys.PAYEES(),
  AUTOCOMPLETE: (query) => `payee_autocomplete_${query.toLowerCase()}`,
  USAGE_STATS: () => 'payee_usage_stats',
  POPULAR_PAYEES: () => 'popular_payees',
  SESSION_DATA: () => 'payee_session_data'
};

/**
 * Payee usage tracking
 */
class PayeeUsageTracker {
  constructor() {
    this.usageStats = this.loadUsageStats();
  }

  loadUsageStats() {
    try {
      const stats = localStorage.getItem(PayeeCacheKeys.USAGE_STATS());
      return stats ? JSON.parse(stats) : {};
    } catch (error) {
      console.warn('Failed to load payee usage stats:', error);
      return {};
    }
  }

  saveUsageStats() {
    try {
      localStorage.setItem(PayeeCacheKeys.USAGE_STATS(), JSON.stringify(this.usageStats));
    } catch (error) {
      console.warn('Failed to save payee usage stats:', error);
    }
  }

  recordUsage(payeeName) {
    if (!PAYEE_CACHE_CONFIG.USAGE_TRACKING) return;

    const key = payeeName.toLowerCase();
    this.usageStats[key] = {
      name: payeeName,
      count: (this.usageStats[key]?.count || 0) + 1,
      lastUsed: Date.now(),
      firstUsed: this.usageStats[key]?.firstUsed || Date.now()
    };

    this.saveUsageStats();
  }

  getPopularPayees() {
    return Object.values(this.usageStats)
      .filter(stat => stat.count >= PAYEE_CACHE_CONFIG.POPULAR_THRESHOLD)
      .sort((a, b) => b.count - a.count)
      .map(stat => stat.name);
  }

  getRecentPayees(limit = 10) {
    return Object.values(this.usageStats)
      .sort((a, b) => b.lastUsed - a.lastUsed)
      .slice(0, limit)
      .map(stat => stat.name);
  }

  getUsageStats() {
    return {
      totalPayees: Object.keys(this.usageStats).length,
      popularPayees: this.getPopularPayees().length,
      totalUsage: Object.values(this.usageStats).reduce((sum, stat) => sum + stat.count, 0)
    };
  }
}

/**
 * Enhanced Payee Cache Service
 */
class PayeeCacheService {
  constructor() {
    this.usageTracker = new PayeeUsageTracker();
    this.debounceTimers = new Map();
    this.prefetchQueue = new Set();
    this.sessionData = this.loadSessionData();
  }

  /**
   * Load session data
   */
  loadSessionData() {
    try {
      const data = sessionStorage.getItem(PayeeCacheKeys.SESSION_DATA());
      return data ? JSON.parse(data) : {
        loadedAt: null,
        payeeCount: 0,
        lastRefresh: null
      };
    } catch {
      return {
        loadedAt: null,
        payeeCount: 0,
        lastRefresh: null
      };
    }
  }

  /**
   * Save session data
   */
  saveSessionData() {
    try {
      sessionStorage.setItem(PayeeCacheKeys.SESSION_DATA(), JSON.stringify(this.sessionData));
    } catch (error) {
      console.warn('Failed to save payee session data:', error);
    }
  }

  /**
   * Get all payees with session-based caching
   */
  async getAllPayees(forceRefresh = false) {
    const cacheKey = PayeeCacheKeys.ALL_PAYEES();
    
    if (!forceRefresh) {
      // Try session storage first (faster than localStorage)
      const sessionCached = sessionStorage.getItem(cacheKey);
      if (sessionCached) {
        try {
          const { data, timestamp } = JSON.parse(sessionCached);
          if (Date.now() - timestamp < PAYEE_CACHE_CONFIG.SESSION_TTL) {
            return data;
          }
        } catch {
          // Continue to localStorage check
        }
      }

      // Try localStorage cache
      const cached = await cacheService.get(cacheKey);
      if (cached) {
        // Store in session storage for faster access
        this.storeInSession(cacheKey, cached);
        return cached;
      }
    }

    return null; // Cache miss - caller should fetch from API
  }

  /**
   * Cache payees with session and persistent storage
   */
  async cachePayees(payees) {
    const cacheKey = PayeeCacheKeys.ALL_PAYEES();
    const data = { payees, timestamp: Date.now() };

    // Store in both session and persistent cache
    this.storeInSession(cacheKey, data);
    
    await cacheService.set(cacheKey, data, {
      ttl: PAYEE_CACHE_CONFIG.SESSION_TTL,
      tags: ['payees'],
      dependencies: ['payee_operations']
    });

    // Update session data
    this.sessionData = {
      loadedAt: Date.now(),
      payeeCount: payees.length,
      lastRefresh: Date.now()
    };
    this.saveSessionData();

    // Schedule prefetch of popular payees
    if (PAYEE_CACHE_CONFIG.PREFETCH_ENABLED) {
      this.schedulePrefetch(payees);
    }
  }

  /**
   * Fast autocomplete search with intelligent caching
   */
  async searchPayees(query, allPayees = null) {
    if (!query || query.length < PAYEE_CACHE_CONFIG.MIN_SEARCH_LENGTH) {
      return this.getRecentAndPopularPayees();
    }

    const normalizedQuery = query.toLowerCase().trim();
    const cacheKey = PayeeCacheKeys.AUTOCOMPLETE(normalizedQuery);

    // Check autocomplete cache first
    const cached = await cacheService.get(cacheKey);
    if (cached) {
      return cached.results;
    }

    // Perform search
    const payees = allPayees || (await this.getAllPayees())?.payees || [];
    const results = this.performSearch(normalizedQuery, payees);

    // Cache autocomplete results
    await cacheService.set(cacheKey, { results, query: normalizedQuery }, {
      ttl: PAYEE_CACHE_CONFIG.AUTOCOMPLETE_TTL,
      tags: ['autocomplete', 'payees']
    });

    return results;
  }

  /**
   * Debounced autocomplete search
   */
  async debouncedSearch(query, callback, allPayees = null) {
    // Clear existing timer for this query
    if (this.debounceTimers.has(query)) {
      clearTimeout(this.debounceTimers.get(query));
    }

    // Set new timer
    const timer = setTimeout(async () => {
      try {
        const results = await this.searchPayees(query, allPayees);
        callback(results, null);
      } catch (error) {
        callback([], error);
      } finally {
        this.debounceTimers.delete(query);
      }
    }, PAYEE_CACHE_CONFIG.DEBOUNCE_DELAY);

    this.debounceTimers.set(query, timer);
  }

  /**
   * Record payee usage for intelligent caching
   */
  recordPayeeUsage(payeeName) {
    this.usageTracker.recordUsage(payeeName);
    
    // Invalidate popular payees cache
    const popularKey = PayeeCacheKeys.POPULAR_PAYEES();
    cacheService.invalidate(popularKey, { reason: 'usage_update' });
  }

  /**
   * Get recent and popular payees for empty search
   */
  async getRecentAndPopularPayees() {
    const cacheKey = PayeeCacheKeys.POPULAR_PAYEES();
    
    const cached = await cacheService.get(cacheKey);
    if (cached) {
      return cached.results;
    }

    const popular = this.usageTracker.getPopularPayees();
    const recent = this.usageTracker.getRecentPayees();
    
    // Combine and deduplicate
    const combined = [...new Set([...popular, ...recent])];
    const results = combined
      .slice(0, PAYEE_CACHE_CONFIG.MAX_RESULTS)
      .map(name => ({ name, type: 'suggestion' }));

    // Cache the results
    await cacheService.set(cacheKey, { results }, {
      ttl: PAYEE_CACHE_CONFIG.AUTOCOMPLETE_TTL,
      tags: ['popular', 'payees']
    });

    return results;
  }

  /**
   * Invalidate payee caches
   */
  async invalidatePayeeCache(reason = 'manual') {
    // Clear all payee-related caches
    await cacheService.invalidate(/payee/, { reason });
    
    // Clear session storage
    sessionStorage.removeItem(PayeeCacheKeys.ALL_PAYEES());
    sessionStorage.removeItem(PayeeCacheKeys.SESSION_DATA());
    
    // Reset session data
    this.sessionData = {
      loadedAt: null,
      payeeCount: 0,
      lastRefresh: null
    };

    console.log(`Payee cache invalidated (reason: ${reason})`);
  }

  /**
   * Get cache performance metrics
   */
  getMetrics() {
    const usageStats = this.usageTracker.getUsageStats();
    const sessionInfo = {
      ...this.sessionData,
      sessionCacheSize: this.getSessionCacheSize()
    };

    return {
      usage: usageStats,
      session: sessionInfo,
      debounceTimers: this.debounceTimers.size,
      prefetchQueue: this.prefetchQueue.size
    };
  }

  /**
   * Private methods
   */

  storeInSession(key, data) {
    try {
      const sessionData = {
        data,
        timestamp: Date.now()
      };
      sessionStorage.setItem(key, JSON.stringify(sessionData));
    } catch (error) {
      console.warn('Failed to store in session:', error);
    }
  }

  performSearch(query, payees) {
    const results = [];
    const queryLower = query.toLowerCase();

    for (const payee of payees) {
      const nameLower = payee.name.toLowerCase();
      
      // Exact match (highest priority)
      if (nameLower === queryLower) {
        results.unshift({ ...payee, matchType: 'exact' });
        continue;
      }
      
      // Starts with query (high priority)
      if (nameLower.startsWith(queryLower)) {
        results.push({ ...payee, matchType: 'prefix' });
        continue;
      }
      
      // Contains query (lower priority)
      if (nameLower.includes(queryLower)) {
        results.push({ ...payee, matchType: 'contains' });
      }
      
      // Stop if we have enough results
      if (results.length >= PAYEE_CACHE_CONFIG.MAX_RESULTS) {
        break;
      }
    }

    // Sort by match type and usage frequency
    return results
      .sort((a, b) => {
        // Sort by match type first
        const matchOrder = { exact: 0, prefix: 1, contains: 2 };
        const matchDiff = matchOrder[a.matchType] - matchOrder[b.matchType];
        if (matchDiff !== 0) return matchDiff;
        
        // Then by usage frequency
        const aUsage = this.usageTracker.usageStats[a.name.toLowerCase()]?.count || 0;
        const bUsage = this.usageTracker.usageStats[b.name.toLowerCase()]?.count || 0;
        return bUsage - aUsage;
      })
      .slice(0, PAYEE_CACHE_CONFIG.MAX_RESULTS);
  }

  schedulePrefetch(payees) {
    setTimeout(() => {
      // Prefetch popular payee autocomplete results
      const popular = this.usageTracker.getPopularPayees();
      popular.slice(0, 5).forEach(payeeName => {
        this.prefetchQueue.add(payeeName.substring(0, 3)); // Prefetch 3-char prefixes
      });
      
      this.processPrefetchQueue(payees);
    }, PAYEE_CACHE_CONFIG.PREFETCH_DELAY);
  }

  async processPrefetchQueue(payees) {
    for (const query of this.prefetchQueue) {
      try {
        await this.searchPayees(query, payees);
      } catch (error) {
        console.warn(`Prefetch failed for query: ${query}`, error);
      }
    }
    this.prefetchQueue.clear();
  }

  getSessionCacheSize() {
    try {
      let size = 0;
      for (let key in sessionStorage) {
        if (key.includes('payee')) {
          size += sessionStorage[key].length;
        }
      }
      return `${(size / 1024).toFixed(2)} KB`;
    } catch {
      return 'Unknown';
    }
  }
}

// Create singleton instance
const payeeCacheService = new PayeeCacheService();

// Export as default for consistency
export default payeeCacheService;
export { PAYEE_CACHE_CONFIG, PayeeCacheKeys };